import { forwardRef } from "react";

export const PALETTE = [
  { c: "#DC2626", bg: "#FEF2F2", b: "#FCA5A5" },
  { c: "#2563EB", bg: "#EFF6FF", b: "#93C5FD" },
  { c: "#059669", bg: "#ECFDF5", b: "#6EE7B7" },
  { c: "#D97706", bg: "#FFFBEB", b: "#FCD34D" },
  { c: "#7C3AED", bg: "#F5F3FF", b: "#C4B5FD" },
  { c: "#0891B2", bg: "#ECFEFF", b: "#67E8F9" },
];

const W = 1200, H = 860, CX = 600, CY = 430, RX = 400, RY = 285;

const pos = (i, n) => {
  const a = (i / n) * Math.PI * 2 - Math.PI / 2;
  return { x: CX + RX * Math.cos(a), y: CY + RY * Math.sin(a) };
};

const Branch = ({ b, i, p, still }) => {
  const col = PALETTE[i % PALETTE.length];
  return (
    <div
      data-testid={still ? undefined : `mindmap-branch-${i}`}
      className={`absolute w-[270px] bg-white ${still ? "" : "rise"}`}
      style={{ left: p.x, top: p.y, ...(still ? { transform: "translate(-50%,-50%)" } : { translate: "-50% -50%" }), border: `1.5px solid ${col.c}`, animationDelay: `${0.15 + i * 0.08}s` }}
    >
      <div className="flex items-center gap-2 px-3 py-2" style={{ background: col.c }}>
        <span className="text-xl leading-none">{b.emoji}</span>
        <span className="font-display font-bold text-white text-[15px] leading-tight tracking-tight">{b.title}</span>
        <span className="ml-auto font-mono text-[10px] text-white/80">0{i + 1}</span>
      </div>
      <ul className="p-2.5 space-y-1.5" style={{ background: col.bg }}>
        {(b.children || []).map((c, j) => (
          <li key={`${c.text}-${j}`} className="flex items-start gap-2 bg-white px-2 py-1.5 text-[12.5px] leading-snug text-zinc-800" style={{ borderLeft: `3px solid ${col.c}` }}>
            <span className="text-base leading-none">{c.emoji}</span>
            <span>{c.text}</span>
          </li>
        ))}
      </ul>
      {b.keyword && (
        <div className="px-3 py-1 font-mono text-[10px] uppercase tracking-widest" style={{ color: col.c, background: "white", borderTop: `1px solid ${col.b}` }}>
          ★ {b.keyword}
        </div>
      )}
    </div>
  );
};

export const MindMap = forwardRef(({ map, still, framed = true }, ref) => {
  const branches = map?.branches || [];
  const n = branches.length || 1;
  return (
    <div className={framed ? "w-full overflow-x-auto border border-zinc-200 bg-white" : ""} data-testid={still ? undefined : "step-3-mindmap-canvas"}>
      <div ref={ref} className="relative bg-white" style={{ width: W, height: H }}>
        <div className="absolute inset-0 swiss-grid-bg opacity-40" />
        <svg className="absolute inset-0" width={W} height={H}>
          {branches.map((b, i) => {
            const p = pos(i, n);
            const col = PALETTE[i % PALETTE.length].c;
            const mx = (CX + p.x) / 2;
            return (
              <g key={`${b.title}-${i}`}>
                <path d={`M${CX},${CY} C${mx},${CY} ${mx},${p.y} ${p.x},${p.y}`} fill="none" stroke={col} strokeWidth="3" {...(still ? {} : { pathLength: "1", className: "draw-path", style: { animationDelay: `${i * 0.08}s` } })} />
                <circle cx={CX} cy={CY} r="5" fill={col} />
              </g>
            );
          })}
        </svg>
        <div
          data-testid={still ? undefined : "mindmap-center"}
          className={`absolute w-[250px] bg-zinc-950 text-white text-center px-5 py-5 ${still ? "" : "rise"}`}
          style={{ left: CX, top: CY, ...(still ? { transform: "translate(-50%,-50%)" } : { translate: "-50% -50%" }) }}
        >
          <div className="text-4xl mb-2 leading-none">{map?.emoji}</div>
          <div className="font-display font-extrabold text-xl leading-tight tracking-tight">{map?.title}</div>
          <div className="mt-3 flex gap-0.5 justify-center">
            {branches.map((b, i) => <span key={`${b.title}-${i}`} className="h-1 w-6" style={{ background: PALETTE[i % PALETTE.length].c }} />)}
          </div>
        </div>
        {branches.map((b, i) => <Branch key={`${b.title}-${i}`} b={b} i={i} p={pos(i, n)} still={still} />)}
        {!still && <div className="absolute left-5 bottom-4 font-mono text-[10px] uppercase tracking-widest text-zinc-400">Aurora School · Mappa di studio</div>}
      </div>
    </div>
  );
});
MindMap.displayName = "MindMap";
